import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import IconButton from "@common/components/IconButton";
import MaterialIcon from "@common/components/MaterialIcon";
import { useSession } from "@jkast90/kast-common/auth";
import type { CloudSaveMeta } from "../emulator/cloudSaves";
import { ejsStates } from "../emulator/ejsStates";
import { KEPT_SOURCE_LABEL, keptFor, listKept, type KeptSave } from "../emulator/keptSaves";
import { ejsSystemOfName, isEjsSystem, rememberSystem } from "../emulator/ejs";
import { playFile, playUrl as playerUrl } from "../emulator/pending";
import { displayName, downloadBytes, fileExtension, isRomFile, romBaseName, saveFileName, stateFileName } from "../emulator/saves";
import { slotStore } from "../emulator/slots";
import { downloadCloudRom, uploadToLibrary } from "../library/cloudLibrary";
import GameRow from "../library/GameRow";
import { useCover } from "../library/useCover";
import { signedInAdmin } from "../library/admin";
import { ago, type GameSummary } from "../library/dashboard";
import PlayerLink from "../library/PlayerLink";
import type { StoredFile } from "../library/idbfs";
import { listLocalGames, playsHere, readLocalRom, SYSTEM_LABEL, SYSTEM_ORDER, type LocalGame, type System } from "../library/localLibrary";
import { ejsLocalSaves, mgbaLocalSaves } from "../library/localSaves";
import { deleteGameSave, deleteSlot, readGameSave, readSlot, restoreKeptSave, writeGameSave, writeSlot } from "../library/librarySaves";
import { useConfirm } from "@common/components/ConfirmDialog";
import {
  buildLibrary,
  bySystem,
  filterLibrary,
  LIBRARY_SORTS,
  sortLibrary,
  type LibraryFilter,
  type LibraryGame,
  type LibrarySort,
} from "../library/myLibrary";
import { formatPlayed, playKey } from "../library/playtime";
import { errorText, libraryKeys, queryKeys, useCloudRoms, useCloudSaves, useDashboard, useMyPlaytime } from "../library/queries";
import { followRenames } from "../library/renames";
import { here, Here, desktop, isDesktop, isNative, pickedNote, type Transfer } from "../desktop/desktop";
import ScanFolderButton from "../library/ScanFolderButton";
import Cover from "../library/Cover";
import KeyHelp from "../emulator/components/KeyHelp";
import Dropdown from "@common/components/Dropdown";
import ConsoleIcon from "../library/ConsoleIcon";
import { setPref } from "@common/preferences";
import { session, signIn } from "../auth/session";
import { useOnline } from "@common/online";

// Every game you have: in this browser, in the cloud library, or known only
// from a save or play time. A list on the left, the chosen game on the right
// (one at a time on a phone). Replaces the separate My games and My saves.

interface LocalData {
  games: LocalGame[];
  mgbaSaves: StoredFile[];
  ejsSaves: Map<string, StoredFile>;
  ejsStates: [string, number][];
  kept: KeptSave[];
}

const SORT_KEY = "pocket-play.librarySort";
// Stable, so the library isn't rebuilt on every render while signed out.
const NO_SAVES: CloudSaveMeta[] = [];

async function readLocal(): Promise<LocalData> {
  const games = await listLocalGames().catch(() => [] as LocalGame[]);
  const ejsGames = games.filter((g) => g.system === "n64").map((g) => g.name);
  const [mgbaSaves, ejsSaves, stateFiles, kept] = await Promise.all([
    mgbaLocalSaves().catch(() => [] as StoredFile[]),
    ejsLocalSaves(ejsGames).catch(() => new Map<string, StoredFile>()),
    ejsStates.list().catch(() => [] as string[]),
    listKept().catch(() => [] as KeptSave[]),
  ]);
  // A state's time is in its slot's stats; reading the state itself would
  // load megabytes.
  const held = new Set(stateFiles);
  const states: [string, number][] = [];
  for (const game of ejsGames) {
    const slots = slotStore.list(game);
    const stats = slotStore.stats(game, slots);
    for (const slot of slots) {
      const file = stateFileName(game, slot);
      if (held.has(file)) states.push([file, stats[slot]?.at ?? 0]);
    }
  }
  return { games, mgbaSaves, ejsSaves, ejsStates: states, kept };
}

const when = (ms: number) => ago(new Date(ms).toISOString());

function ListItem({ game, selected, onSelect }: { game: LibraryGame; selected: boolean; onSelect: () => void }) {
  return (
    <GameRow
      name={game.name}
      system={game.system}
      selected={selected}
      onSelect={onSelect}
      meta={[game.slots.length > 0 && `${game.slots.length} slot${game.slots.length === 1 ? "" : "s"}`, game.play && formatPlayed(game.play.seconds)]
        .filter(Boolean)
        .join(" · ")}
      where={
        <>
          {game.local && <MaterialIcon name="computer" />}
          {game.cloud && <MaterialIcon name="cloud" />}
        </>
      }
    />
  );
}

/** A spinner, with the percentage out of a progress line such as "Uploading 45%". */
function Busy({ text }: { text: string }) {
  const percent = /\d+%/.exec(text)?.[0];
  return (
    <>
      <span className="spinner" aria-hidden="true" />
      {percent && ` ${percent}`}
    </>
  );
}

function Detail({
  game,
  kept,
  everyone,
  signedIn,
  onBack,
  onChanged,
}: {
  game: LibraryGame;
  /** This game's kept battery saves, newest first. */
  kept: KeptSave[];
  /** Every player's time on this game, from the dashboard; null if nobody has played it. */
  everyone: GameSummary | null;
  signedIn: boolean;
  onBack: () => void;
  /** Saves changed: read them again. */
  onChanged: () => void;
}) {
  const me = signedInAdmin().sub;
  const players = [...(everyone?.players ?? [])].sort((a, b) => b.seconds - a.seconds);
  const cover = useCover(game.name);
  const picker = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();
  // The upload dialog's console, while it's open.
  const [uploadAs, setUploadAs] = useState<System | null>(null);
  // An upload or an app download under way, as the text its button shows.
  const [transfer, setTransfer] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // One hidden file picker, aimed at the in-game save or a slot.
  const filePicker = useRef<HTMLInputElement>(null);
  const uploadTo = useRef<"save" | number>("save");
  const [shownKey, setShownKey] = useState(game.key);
  if (shownKey !== game.key) {
    setShownKey(game.key);
    setProgress(null);
    setMessage(null);
    setDone(null);
  }

  const run = async (work: () => Promise<string | null>, failed: string) => {
    setMessage(null);
    setDone(null);
    setBusy(true);
    try {
      const text = await work();
      if (text) setDone(text);
      onChanged();
    } catch (e) {
      setMessage(e instanceof Error && !(e instanceof TypeError) ? e.message : errorText(e, failed));
    } finally {
      setBusy(false);
    }
  };

  const pickFile = (target: "save" | number) => {
    uploadTo.current = target;
    filePicker.current?.click();
  };

  const upload = (file: File) => {
    const target = uploadTo.current;
    run(async () => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (target === "save") {
        const where = await writeGameSave(game, bytes, signedIn);
        return where === "here" ? `Save replaced in ${here}.` : "Save uploaded to the cloud; it comes down next time you play.";
      }
      await writeSlot(game, target, bytes, signedIn);
      return `${names[target] ?? `Slot ${target}`} replaced.`;
    }, "Upload failed.");
  };

  const showTransfer = ({ phase, fraction }: Transfer) =>
    setTransfer(`${phase === "hashing" ? "Checking" : phase === "uploading" ? "Uploading" : "Downloading"} ${Math.round(fraction * 100)}%`);

  // Sends a game only this device has to the shared library, as the console
  // the player chose. The app streams its own games from disk.
  const share = async (system: System) => {
    setUploadAs(null);
    setMessage(null);
    setDone(null);
    setTransfer("Uploading…");
    try {
      let result;
      if (isDesktop && isNative(game.system)) {
        result = await desktop.uploadGame(game.name, system, session.bearer() ?? "", showTransfer);
      } else {
        const file = await readLocalRom(game.name);
        if (!file) throw new Error(`${game.name} isn't in ${here} any more.`);
        result = await uploadToLibrary(file, system);
      }
      if (result === "too-large") setMessage("Too big for the cloud library (32 MB at most).");
      else {
        setDone(result === "uploaded" ? "Added to the cloud library." : "It's already in the cloud library.");
        libraryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      }
    } catch (e) {
      setMessage(e instanceof Error && !(e instanceof TypeError) ? e.message : errorText(e, "Upload failed."));
    } finally {
      setTransfer(null);
    }
  };

  // A disc game starting in its own window: the step it's on, until the
  // window is up.
  const [launching, setLaunching] = useState<string | null>(null);
  const launch = async () => {
    setMessage(null);
    setLaunching("Starting");
    try {
      await desktop.play(game.name, game.system ?? "", setLaunching);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setLaunching(null);
    }
  };

  // A cloud disc game into the app's games folder.
  const fetchToMac = async () => {
    if (!game.cloud) return;
    setMessage(null);
    setTransfer("Downloading…");
    try {
      await desktop.downloadGame(game.cloud, session.bearer() ?? "", showTransfer);
      onChanged();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setTransfer(null);
    }
  };

  const nextSlot = Math.max(0, ...game.slots.map((s) => s.slot)) + 1;

  const names = game.local ? slotStore.names(game.name, game.slots.map((s) => s.slot)) : {};
  const active = game.local ? slotStore.active(game.name) : null;
  // A full page load (an <a>, not a router Link): only /play is served
  // cross-origin isolated (pending.ts).
  const playUrl = (slot?: number | null, paused = false) =>
    playerUrl({ game: game.name, ...(slot ? { slot: String(slot) } : {}), ...(paused ? { paused: "1" } : {}) });

  const install = async () => {
    if (!game.cloud || progress !== null) return;
    setProgress(0);
    setMessage(null);
    try {
      await playFile(await downloadCloudRom(game.cloud, setProgress));
    } catch (e) {
      setMessage(errorText(e, "Download failed."));
      setProgress(null);
    }
  };

  const downloadSave = () =>
    run(async () => {
      downloadBytes(saveFileName(game.key), await readGameSave(game));
      return null;
    }, "Could not download the save.");

  const hasSave = game.localSave !== null || game.cloudSaveAt !== null;
  const where = (local: boolean, cloud: boolean) =>
    [local && (isDesktop ? "this Mac" : `${here}`), cloud && "cloud"].filter(Boolean).join(" and ") || "nowhere";

  return (
    <article className="library-detail">
      <button type="button" className="btn library-back" onClick={onBack}>
        <MaterialIcon name="arrow_back" /> All games
      </button>
      <header className="library-hero">
        {cover && <img src={cover} alt="" className="library-hero-backdrop" />}
        <div className="library-hero-body">
          {cover ? (
            <img src={cover} alt="" className="library-hero-cover" />
          ) : (
            <span className="library-hero-cover blank">
              <MaterialIcon name="videogame_asset" />
            </span>
          )}
          <div className="library-hero-text">
            <h1 title={game.name}>{displayName(game.name)}</h1>
            <p className="muted">{[game.system && SYSTEM_LABEL[game.system], game.name].filter(Boolean).join(" · ")}</p>
            <div className="library-actions">
              {game.local && isDesktop && isNative(game.system) ? (
                <button
                  type="button"
                  className="btn primary library-play icon-btn"
                  disabled={launching !== null}
                  aria-label={launching ? `${launching}…` : "Play"}
                  data-tip={launching ? `${launching}…` : "Play"}
                  onClick={launch}
                >
                  {launching ? <span className="spinner" aria-hidden="true" /> : <MaterialIcon name="play_arrow" />}
                </button>
              ) : !playsHere(game.system) ? (
                <span className="muted small">Plays in the CloudBoy desktop app.</span>
              ) : game.local ? (
                <>
                  <a href={playUrl(active)} className="btn primary library-play icon-btn" aria-label="Play" data-tip="Play">
                    <MaterialIcon name="play_arrow" />
                  </a>
                  <a
                    href={playUrl(active, true)}
                    className="btn library-play icon-btn"
                    aria-label="Open paused"
                    data-tip="Open paused: the game loads and waits for Resume"
                  >
                    <MaterialIcon name="pause" />
                  </a>
                </>
              ) : game.cloud && signedIn && isNative(game.system) ? (
                <button
                  type="button"
                  className="btn primary library-play icon-btn"
                  disabled={transfer !== null}
                  aria-label={transfer ?? "Download to this Mac"}
                  data-tip={transfer ?? "Download to this Mac"}
                  onClick={fetchToMac}
                >
                  {transfer ? <Busy text={transfer} /> : <MaterialIcon name="download" />}
                </button>
              ) : game.cloud && signedIn ? (
                <>
                  <button
                    type="button"
                    className="btn primary library-play icon-btn"
                    disabled={progress !== null}
                    aria-label={progress === null ? "Download and play" : `Downloading ${Math.round(progress * 100)}%`}
                    data-tip={progress === null ? "Download and play" : `Downloading ${Math.round(progress * 100)}%`}
                    onClick={install}
                  >
                    {progress === null ? (
                      <MaterialIcon name="download" />
                    ) : (
                      <>
                        <span className="spinner" aria-hidden="true" /> {Math.round(progress * 100)}%
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn icon-btn"
                    disabled={progress !== null}
                    aria-label="Use a copy on this computer"
                    data-tip="Use a copy on this computer"
                    onClick={() => picker.current?.click()}
                  >
                    <MaterialIcon name="folder_open" />
                  </button>
                  <input
                    ref={picker}
                    type="file"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file || !(isRomFile(file.name) || ejsSystemOfName(file.name))) return;
                      // The library game's name with the picked file's
                      // extension, so its saves line up with the cloud copy.
                      const named = `${romBaseName(game.name)}.${fileExtension(file.name)}`;
                      // The library knows the console even when the file (a .7z) can't say.
                      if (game.system && isEjsSystem(game.system)) rememberSystem(named, game.system);
                      void playFile(new File([file], named, { type: file.type }));
                    }}
                  />
                </>
              ) : (
                <span className="muted small">
                  {game.cloud ? "Sign in to download it from the cloud library." : `Not in ${here} or the cloud library.`}
                </span>
              )}
              {game.local && !game.cloud && signedIn && (
                <button
                  type="button"
                  className="btn icon-btn"
                  disabled={transfer !== null}
                  aria-label={transfer ?? "Upload to library"}
                  data-tip={transfer ?? "Upload to library"}
                  onClick={() => setUploadAs(game.system ?? "gba")}
                >
                  {transfer ? <Busy text={transfer} /> : <MaterialIcon name="cloud_upload" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {message && <p className="notice error">{message}</p>}
      {done && <p className="notice">{done}</p>}
      {confirmDialog}
      {launching && (
        <div className="modal-overlay launch" role="status" aria-live="polite">
          <div className="launch-card">
            <Cover name={game.name} className="screen-cover" />
            <div className="opening-line">
              <span className="spinner" aria-hidden="true" />
              <div className="opening-text">
                <strong>{displayName(game.name)}</strong>
                <span className="muted small">{launching}…</span>
                {game.system && <span className="muted small">{SYSTEM_LABEL[game.system]} opens in its own window.</span>}
              </div>
            </div>
          </div>
        </div>
      )}
      {uploadAs && (
        <div className="modal-overlay" onClick={() => setUploadAs(null)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              share(uploadAs);
            }}
          >
            <div className="modal-header">
              <h2>Upload to library</h2>
              <button type="button" className="btn icon-btn" onClick={() => setUploadAs(null)} aria-label="Close">
                <MaterialIcon name="close" />
              </button>
            </div>
            <p>
              Everyone in the cloud library gets <strong>{displayName(game.name)}</strong>. Which console is it for?
            </p>
            <Dropdown
              value={uploadAs}
              options={SYSTEM_ORDER.map((system) => ({ value: system, label: SYSTEM_LABEL[system], icon: <ConsoleIcon system={system} /> }))}
              onChange={setUploadAs}
              label="Console"
            />
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setUploadAs(null)}>
                Cancel
              </button>
              <button type="submit" className="btn primary">
                <MaterialIcon name="cloud_upload" /> Upload
              </button>
            </div>
          </form>
        </div>
      )}
      <input
        ref={filePicker}
        type="file"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) upload(file);
        }}
      />

      <section className="library-stats">
        <div>
          <span className="muted small">You</span>
          <strong>{game.play ? formatPlayed(game.play.seconds) : signedIn ? "Not played" : "Sign in to see"}</strong>
          {game.play && <span className="muted small">last played {ago(game.play.lastPlayed)}</span>}
        </div>
        <div>
          <span className="muted small">Everyone</span>
          <strong>{everyone ? formatPlayed(everyone.seconds) : signedIn ? "Not played" : "Sign in to see"}</strong>
          {everyone && (
            <span className="muted small">
              {players.length} {players.length === 1 ? "player" : "players"} · last {ago(everyone.lastPlayed)}
            </span>
          )}
        </div>
        <div>
          <span className="muted small">Where</span>
          <strong>{where(game.local, !!game.cloud)}</strong>
        </div>
        <div className="wide">
          <span className="muted small">Who's playing</span>
          {players.length > 0 ? (
            <ul className="library-players">
              {players.map((player) => (
                <li key={player.sub} className={player.sub === me ? "me" : undefined}>
                  <strong>
                    <PlayerLink sub={player.sub} email={player.email} name={player.name} me={me} />
                  </strong>
                  <span className="muted small">
                    {formatPlayed(player.seconds)} · {ago(player.lastPlayed)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <strong>{signedIn ? "Nobody yet" : "Sign in to see"}</strong>
          )}
        </div>
      </section>

      {game.system && game.system !== "xbox" && (
        <section className="panel keys">
          <h2>Keyboard</h2>
          <KeyHelp system={game.system} game={game.name} />
          <p className="muted small">
            Click a key to change it; the same keys work in the browser and the desktop app.
            {isNative(game.system) && " In a game window F5 saves a state and F8 loads it."}
          </p>
        </section>
      )}
      {isNative(game.system) && !isDesktop ? (
        <section className="panel">
          <h2>Saves</h2>
          <p className="muted small">PlayStation, PSP and Xbox games play in the CloudBoy desktop app, which keeps their saves.</p>
        </section>
      ) : isNative(game.system) ? (
        <>
        <section className="panel">
          <h2>Saves</h2>
          <p className="muted small">
            Memory cards and PSP saves are kept on this Mac, written while you play; Xbox saves live on xemu's hard drive
            image. Slots and cloud sync come to the desktop app later.
          </p>
        </section>
        </>
      ) : (
        <>
      <section className="panel">
        <div className="section-head">
          <h2>In-game save</h2>
          <div className="btn-group">
            {hasSave && (
              <IconButton
                icon="download"
                label="Download the save (.sav)"
                disabled={busy || (!game.localSave && !signedIn)}
                onClick={downloadSave}
              />
            )}
            <IconButton icon="file_upload" label="Upload a save (.sav) to replace this one" disabled={busy} onClick={() => pickFile("save")} />
            {hasSave && (
              <IconButton
                icon="delete"
                label="Delete the in-game save"
                className="danger"
                disabled={busy || (game.cloudSaveAt !== null && !signedIn)}
                onClick={async () => {
                  const where =
                    game.localSave && game.cloudSaveAt !== null ? `on ${here} and in the cloud` : game.cloudSaveAt !== null ? "in the cloud" : `on ${here}`;
                  const ok = await confirm({
                    title: `Delete the save for ${displayName(game.name)}?`,
                    message: `Your in-game progress is deleted ${where}. Save states in slots stay, and a copy of the save stays under Kept saves on ${here}.`,
                  });
                  if (!ok) return;
                  run(async () => {
                    await deleteGameSave(game, signedIn);
                    return "In-game save deleted.";
                  }, "Could not delete the save.");
                }}
              />
            )}
          </div>
        </div>
        {hasSave ? (
          <p className="muted small">
            {[
              game.localSave && `${Here}, ${when(game.localSave.updatedAt)}`,
              game.cloudSaveAt !== null && `Cloud, ${when(game.cloudSaveAt)}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : (
          <p className="muted small">
            {signedIn ? "None yet. Save in the game and it shows up here." : `None on ${here}. Sign in to see the cloud's too.`}
          </p>
        )}
      </section>

      {kept.length > 0 && (
        <section className="panel" aria-labelledby="kept-saves">
          <h2 id="kept-saves">Kept saves</h2>
          <p className="muted small">
            In-game saves that were replaced or put aside, kept on {here}. Restoring one makes it the game's save, and the current
            one is kept here in its place.
          </p>
          <ul className="library-slots">
            {kept.map((save) => (
              <li key={save.id}>
                <div>
                  <strong>{KEPT_SOURCE_LABEL[save.source]}</strong>
                  <span className="muted small">
                    {[`kept ${when(save.keptAt)}`, save.savedAt !== null && `saved ${when(save.savedAt)}`].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <div className="btn-group">
                  <IconButton
                    icon="history"
                    label="Restore this save"
                    disabled={busy}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Restore this save for ${displayName(game.name)}?`,
                        message: `It becomes the game's in-game save. The current one is kept here, so you can switch back.`,
                        action: "Restore",
                        danger: false,
                      });
                      if (!ok) return;
                      run(async () => {
                        const where = await restoreKeptSave(game, save, signedIn);
                        return where === "here" ? `Save restored in ${here}.` : "Save restored to the cloud; it comes down next time you play.";
                      }, "Could not restore the save.");
                    }}
                  />
                  <IconButton
                    icon="download"
                    label="Download this save (.sav)"
                    disabled={busy}
                    onClick={() => downloadBytes(save.file, save.bytes)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <div className="section-head">
          <h2>Save states</h2>
          <IconButton icon="add" label="Add a state from a file, as a new slot" disabled={busy} onClick={() => pickFile(nextSlot)} />
        </div>
        {game.slots.length === 0 ? (
          <p className="muted small">{signedIn ? "No states saved." : `No states on ${here}. Sign in to see the cloud's too.`}</p>
        ) : (
          <ul className="library-slots">
            {game.slots.map((s) => (
              <li key={s.slot} className={s.slot === active ? "active" : undefined}>
                <div>
                  <strong>{names[s.slot] ?? `Slot ${s.slot}`}</strong>
                  <span className="muted small">
                    {s.at ? when(s.at) : "time unknown"} · {where(s.local, s.inCloud)}
                  </span>
                </div>
                <div className="btn-group">
                  {game.local && (
                    <a href={playUrl(s.slot)} className="btn icon-btn" data-tip="Play from this slot" aria-label={`Play from ${names[s.slot] ?? `slot ${s.slot}`}`}>
                      <MaterialIcon name="play_arrow" />
                    </a>
                  )}
                  <IconButton
                    icon="download"
                    label="Download this state"
                    disabled={busy || (!s.local && !signedIn)}
                    onClick={() =>
                      run(async () => {
                        downloadBytes(stateFileName(game.name, s.slot), await readSlot(game, s));
                        return null;
                      }, "Could not download the state.")
                    }
                  />
                  <IconButton icon="file_upload" label="Replace this state from a file" disabled={busy} onClick={() => pickFile(s.slot)} />
                  <IconButton
                    icon="delete"
                    label="Delete this state"
                    className="danger"
                    disabled={busy || (s.inCloud && !signedIn)}
                    onClick={async () => {
                      const label = names[s.slot] ?? `Slot ${s.slot}`;
                      const where = s.local && s.inCloud ? `on ${here} and in the cloud` : s.inCloud ? "in the cloud" : `on ${here}`;
                      if (!(await confirm({ title: `Delete ${label}?`, message: `Its saved state is deleted ${where}. This can't be undone.` }))) return;
                      run(async () => {
                        await deleteSlot(game, s, signedIn);
                        return `${label} deleted.`;
                      }, "Could not delete the state.");
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        {game.system === "n64" && (
          <p className="muted small">States for this console stay in {here}: they are too big for cloud saves.</p>
        )}
      </section>
        </>
      )}
    </article>
  );
}

export default function LibraryPage() {
  const isAuthenticated = useSession().status === "signedIn";
  const online = useOnline();
  const [params, setParams] = useSearchParams();
  const [local, setLocal] = useState<LocalData | null>(null);
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const [search, setSearch] = useState("");
  // The order is remembered; the filters start over each visit.
  const [sort, setSortState] = useState<LibrarySort>(() => {
    const saved = localStorage.getItem(SORT_KEY);
    return LIBRARY_SORTS.some((s) => s.value === saved) ? (saved as LibrarySort) : "recent";
  });
  const setSort = (value: LibrarySort) => {
    setSortState(value);
    setPref(SORT_KEY, value);
  };
  const [addNote, setAddNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const addInput = useRef<HTMLInputElement>(null);
  const roms = useCloudRoms(isAuthenticated);
  const plays = useMyPlaytime(isAuthenticated);
  const feed = useDashboard(isAuthenticated);
  const cloudSaveList = useCloudSaves(isAuthenticated);
  const saves = cloudSaveList.data ?? NO_SAVES;
  const queryClient = useQueryClient();

  // Reads of browser storage can finish out of order; only the latest lands.
  const localReads = useRef(0);
  const refreshLocal = useCallback(() => {
    const read = ++localReads.current;
    readLocal().then((data) => {
      if (read === localReads.current) setLocal(data);
    });
  }, []);
  useEffect(refreshLocal, [refreshLocal]);

  // After a save is written or deleted here: browser storage again, and the
  // cloud's list refetched (an invalidation drops a list still in flight).
  const reload = () => {
    refreshLocal();
    if (isAuthenticated) void queryClient.invalidateQueries({ queryKey: queryKeys.cloudSaves });
  };

  // A library game an admin renamed takes this browser's copy, saves and
  // slots along to its new name.
  useEffect(() => {
    if (!local?.games.length || !roms.data?.length) return;
    followRenames(local.games, roms.data).then(
      (moved) => {
        if (moved) refreshLocal();
      },
      () => {},
    );
  }, [local, roms.data, refreshLocal]);

  const games = useMemo(
    () =>
      local &&
      buildLibrary({
        local: local.games,
        cloudRoms: isAuthenticated ? (roms.data ?? []) : [],
        plays: isAuthenticated ? (plays.data ?? []) : [],
        cloudSaves: isAuthenticated ? saves : [],
        mgbaSaves: local.mgbaSaves,
        ejsSaves: local.ejsSaves,
        ejsStates: local.ejsStates,
        kept: local.kept,
      }),
    [local, roms.data, plays.data, saves, isAuthenticated],
  );

  const systemParam = params.get("system") as System | null;
  const system = systemParam && SYSTEM_ORDER.includes(systemParam) ? systemParam : null;
  const consoles = games ? bySystem(games).map(([s]) => s) : [];
  const everyone = useMemo(
    () => new Map((feed.data?.games ?? []).map((g) => [g.game, { seconds: g.seconds, lastPlayed: g.lastPlayed }])),
    [feed.data],
  );
  // A remembered "everyone" order falls back to your own when signed out.
  const sortInUse = !isAuthenticated && LIBRARY_SORTS.find((s) => s.value === sort)?.everyone ? "recent" : sort;
  const shown = games ? sortLibrary(filterLibrary(games, filter, search, system), sortInUse, everyone) : [];
  const chosenKey = params.get("game");
  const chosen = games?.find((g) => g.key === chosenKey) ?? shown[0] ?? null;
  const choose = (key: string | null) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (key) next.set("game", key);
      else next.delete("game");
      return next;
    });
  const chooseSystem = (value: System | null) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.delete("game");
      if (value) next.set("system", value);
      else next.delete("system");
      return next;
    });
  const total = games?.reduce((sum, g) => sum + (g.play?.seconds ?? 0), 0) ?? 0;
  const savesError = cloudSaveList.error ? errorText(cloudSaveList.error, "Could not load your cloud saves.") : null;
  const error = savesError ?? (roms.error ? errorText(roms.error, "Could not load the cloud library.") : null);

  return (
    <div className={`library${chosenKey ? " showing-detail" : ""}`}>
      <aside className="library-sidebar">
        <div className="library-head">
          <div className="library-title">
            <h1>Library</h1>
            <div className="btn-group">
              <button
                type="button"
                className="btn icon-btn primary"
                aria-label={adding ? "Adding…" : "Add game"}
                data-tip={adding ? "Adding…" : "Add game"}
                disabled={adding}
                onClick={() => {
                  if (!isDesktop) {
                    addInput.current?.click();
                    return;
                  }
                  setAdding(true);
                  desktop
                    .addGames()
                    .then(
                      async (picked) => {
                        setAddNote(pickedNote(picked));
                        if (picked.web) {
                          void playFile(picked.web);
                          return;
                        }
                        // Big archives take a few seconds each to identify, the first time.
                        setLocal(await readLocal());
                      },
                      (e: unknown) => setAddNote(e instanceof Error ? e.message : String(e)),
                    )
                    .finally(() => setAdding(false));
                }}
              >
                {adding ? <span className="spinner" aria-hidden="true" /> : <MaterialIcon name="add" />}
              </button>
              <ScanFolderButton
                className="btn icon-btn"
                iconOnly
                onDone={(note) => {
                  setScanNote(note);
                  readLocal().then(setLocal);
                }}
              />
            </div>
          </div>
          <input
            ref={addInput}
            type="file"
            accept=".gb,.gbc,.gba,.zip,.7z,.z64,.n64,.v64"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file || !(isRomFile(file.name) || ejsSystemOfName(file.name))) return;
              void playFile(file);
            }}
          />
          {isAuthenticated ? (
            total > 0 && (
              <span className="muted small">
                {formatPlayed(total)} played
              </span>
            )
          ) : (
            <button type="button" className="btn small" disabled={!online} onClick={signIn}>
              {online ? "Sign in for cloud games and play time" : "Signing in needs a connection"}
            </button>
          )}
        </div>
        {addNote && <p className="notice error">{addNote}</p>}
        {scanNote && <p className="notice">{scanNote}</p>}
        <input
          className="input"
          type="search"
          placeholder="Search games"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search games"
        />
        <div className="library-filters">
          {/* Where a game is and which console it's for, as one choice:
              a console is picked from the home tiles too (the URL). */}
          <Dropdown
            value={system ?? filter}
            options={[
              { value: "all", label: "All games" },
              { value: "here", label: isDesktop ? "On this Mac" : `In ${here}` },
              { value: "cloud", label: "In the cloud" },
              ...consoles.map((value, i) => ({
                value,
                label: SYSTEM_LABEL[value],
                icon: <ConsoleIcon system={value} />,
                divider: i === 0,
              })),
            ]}
            onChange={(value) => {
              if (value === "all" || value === "here" || value === "cloud") {
                setFilter(value);
                chooseSystem(null);
              } else {
                setFilter("all");
                chooseSystem(value);
              }
            }}
            label="Show"
          />
          <Dropdown
            value={sortInUse}
            options={LIBRARY_SORTS.filter((s) => isAuthenticated || !s.everyone).map(({ value, label }) => ({ value, label }))}
            onChange={setSort}
            label="Sort by"
          />
        </div>
        {error && <p className="notice error">{error}</p>}
        {games === null ? (
          <p className="muted">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="muted small">{games.length === 0
              ? isAuthenticated
                ? "No games yet. Add one, or scan a folder of them, above."
                : `No games on ${here} yet. Add one, or scan a folder of them, above, or sign in for the cloud library.`
              : "Nothing matches."}</p>
        ) : (
          <ul className="library-list">
            {shown.map((game) => (
              <ListItem key={game.key} game={game} selected={game.key === chosen?.key} onSelect={() => choose(game.key)} />
            ))}
          </ul>
        )}
      </aside>

      {chosen && (
        <Detail
          game={chosen}
          kept={keptFor(local?.kept ?? [], chosen.key)}
          everyone={feed.data?.games.find((g) => g.game === playKey(chosen.name)) ?? null}
          signedIn={isAuthenticated}
          onBack={() => choose(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}
